import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import useAuth from 'src/hooks/useAuth'
import { fetchAllFiles } from 'src/api/file/list'
import useUploadedNotAnalysed, {
  RECENT_ANALYSES_KEY,
  RECENT_ANALYSES_OPTIONS,
  fetchRecentAnalyses,
} from 'src/views/NewAnalysis/useUploadedNotAnalysed'
import {
  hiddenKey,
  hideFiles,
  loadHidden,
  readyToAnalyse,
  saveHidden,
  unhideFiles,
} from 'src/views/NewAnalysis/model/ready'
import { getLastWorkflow, lastWorkflowKey } from 'src/views/NewAnalysis/model/workflow'
import { userKeyOf } from 'src/views/NewAnalysis/model/storage'

// ----------------------------------------------------------------------
// The samples of Home's "Ready to analyse" card: uploads of the last 30 days with
// no analysis yet, minus the ones hidden with "Not for analysis" (per user, in
// this browser). Same data and query keys as the panel's "Uploaded, not analysed
// yet" list (useUploadedNotAnalysed); no polling of its own.
//
// refreshKey: changes after a start, so "Last used" is read again.
// ----------------------------------------------------------------------

const useReadySamples = (refreshKey) => {
  const { user } = useAuth() || {}
  const userKey = userKeyOf(user)
  const { candidates, analysed, loaded } = useUploadedNotAnalysed()
  // Observers on the same two queries, only to tell a failed load from a slow one.
  const filesQuery = useQuery(['all-files'], fetchAllFiles, { refetchOnWindowFocus: true })
  const analysesQuery = useQuery(RECENT_ANALYSES_KEY, fetchRecentAnalyses, RECENT_ANALYSES_OPTIONS)
  const failed = !loaded && (filesQuery.isError || analysesQuery.isError)
  const retry = useCallback(() => {
    if (filesQuery.isError) filesQuery.refetch()
    if (analysesQuery.isError) analysesQuery.refetch()
  }, [filesQuery, analysesQuery])

  // ------------------------------------------------------------ hidden samples
  const [hidden, setHidden] = useState(() => loadHidden(userKey))
  const hiddenRef = useRef(hidden)
  hiddenRef.current = hidden
  const hiddenFor = useRef(userKey)
  useEffect(() => {
    if (hiddenFor.current === userKey) return
    hiddenFor.current = userKey
    setHidden(loadHidden(userKey))
  }, [userKey])

  const changeHidden = useCallback(
    (change) => {
      const next = change(hiddenRef.current)
      hiddenRef.current = next
      saveHidden(userKey, next)
      setHidden(next)
    },
    [userKey],
  )
  const hide = useCallback((fileIds) => changeHidden((prev) => hideFiles(prev, fileIds)), [changeHidden])
  const unhide = useCallback((fileIds) => changeHidden((prev) => unhideFiles(prev, fileIds)), [changeHidden])

  // ------------------------------------------------------------ last used workflow
  const [lastWorkflow, setLastWorkflow] = useState(() => getLastWorkflow(userKey))
  useEffect(() => {
    setLastWorkflow(getLastWorkflow(userKey))
  }, [userKey, refreshKey])

  // Another tab hiding a sample or starting an analysis updates this one.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === hiddenKey(userKey)) setHidden(loadHidden(userKey))
      else if (event.key === lastWorkflowKey(userKey)) setLastWorkflow(getLastWorkflow(userKey))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [userKey])

  // ------------------------------------------------------------ samples
  const items = useMemo(
    () => (loaded ? readyToAnalyse(candidates, analysed, { hidden, now: Date.now() }) : []),
    [loaded, candidates, analysed, hidden],
  )
  const openable = useMemo(() => items.filter((item) => !item.verifying), [items])

  return { items, openable, loaded, failed, retry, hide, unhide, lastWorkflow }
}

export default useReadySamples
